import React, { useState } from "react";
import Add from "../img/addAvatar.png";
import { createUserWithEmailAndPassword, updateProfile, signOut } from "firebase/auth";
import { auth, db, storage } from "../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";

const Register = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();
    const displayName = e.target[0].value;
    const email = e.target[1].value;
    const password = e.target[2].value;
    const eventCode = e.target[3].value;
    const file = e.target[4].files[0];

    // Validation
    if (!displayName || !email || !password || !eventCode || !file) {
      toast.error("Please fill in all fields and select an avatar");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      setLoading(false);
      return;
    }

    try {
      toast.loading("Checking account...", { id: "register" });
      
      // First, try to create the account
      // If email exists, this will throw an error and we'll handle it
      try {
        //Create user
        const res = await createUserWithEmailAndPassword(auth, email, password);
        
        // New user created successfully
        toast.loading("Uploading avatar...", { id: "register" });
        
        //Create a unique image name
        const date = new Date().getTime();
        const storageRef = ref(storage, `${displayName + date}`);

        await uploadBytesResumable(storageRef, file).then(() => {
          getDownloadURL(storageRef).then(async (downloadURL) => {
            try {
              //Update profile
              await updateProfile(res.user, {
                displayName,
                photoURL: downloadURL,
              });
              
              toast.loading("Setting up your profile...", { id: "register" });
              
              //create user on firestore
              await setDoc(doc(db, "users", res.user.uid), {
                uid: res.user.uid,
                displayName,
                email,
                photoURL: downloadURL,
                events: [eventCode], // Array of events user is part of
                currentEvent: eventCode, // Currently active event
                // Keep backward compatibility
                eventCode: eventCode,
              });

              //create empty user chats on firestore for this event
              await setDoc(doc(db, "userChats", `${res.user.uid}_${eventCode}`), {});
              
              // Also create the old format for backward compatibility
              await setDoc(doc(db, "userChats", res.user.uid), {});
              
              // Sign out the user so they need to log in
              await signOut(auth);
              
              toast.success("Account created successfully! Please log in to continue.", { id: "register" });
              navigate("/login");
            } catch (err) {
              console.error("Profile setup error:", err);
              toast.error("Error setting up profile. Please try again.", { id: "register" });
              setLoading(false);
            }
          });
        });
        
      } catch (createError) {
        // Account creation failed - check if it's because email already exists
        if (createError.code === 'auth/email-already-in-use') {
          // Email exists - redirect to login with helpful message
          toast.error("An account with this email already exists. Please login to access your account or join new events.", { id: "register" });
          setLoading(false);
          navigate("/login");
          return;
        } else {
          // Other registration errors
          throw createError;
        }
      }
    } catch (err) {
      let errorMessage = "Registration failed. Please try again.";
      
      switch (err.code) {
        case 'auth/email-already-in-use':
          errorMessage = "An account with this email already exists.";
          break;
        case 'auth/invalid-email':
          errorMessage = "Invalid email address format.";
          break;
        case 'auth/weak-password':
          errorMessage = "Password is too weak. Please choose a stronger password.";
          break;
        case 'auth/operation-not-allowed':
          errorMessage = "Email/password accounts are not enabled.";
          break;
        default:
          console.error("Registration error:", err);
      }
      
      toast.error(errorMessage, { id: "register" });
      setLoading(false);
    }
  };

  return (
    <div className="formContainer">
      <div className="formWrapper">
        <span className="logo">Planyt Chat</span>
        <span className="title">Register</span>
        <form onSubmit={handleSubmit}>
          <input required type="text" placeholder="display name" />
          <input required type="email" placeholder="email" />
          <input required type="password" placeholder="password" />
          <input required type="text" placeholder="Event Code" />
          <input required style={{ display: "none" }} type="file" id="file" />
          <label htmlFor="file">
            <img src={Add} alt="" />
            <span>Add an avatar</span>
          </label>
          <button disabled={loading}>
            {loading ? "Creating Account..." : "Sign up"}
          </button>
        </form>
        <p>
          You do have an account? <Link to="/register">Login</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
